<template>
  <div v-if="loading" class="fullscreen-centered">
    Loading...
  </div>
  <div v-else>
    <div class="pb4">
      <ul v-if="loggedIn" class="list-reset flex h6 px1 items-center" style="height: 28px">
        <li class="px1 mb0">
          <a @click="tab = 'published'" :class="{ 'active': tab === 'published' }" class="pointer tab-link">Published</a>
        </li>
        <li class="px1 mb0">
          <a @click="tab = 'drafts'" :class="{ 'active': tab === 'drafts', 'disabled': drafts.length === 0, 'gray': drafts.length === 0 }" class="pointer tab-link">Drafts</a>
        </li>
        <li v-if="tab === 'drafts' && hasUnsubmittedDrafts" class="flex-auto right-align mb0">
          <a @click="uploadAll" class="btn btn-rounded btn-small bg-black white">UPLOAD ALL</a>
        </li>
      </ul>

      <div v-if="tab === 'drafts'">
        <div v-if="drafts.length">
          <ul class="list-reset">
            <draft-preview v-for="draft in drafts" :key="draft.draftKey" :draft="draft"/>
          </ul>
        </div>
        <div v-else class="px2 py1 h6">
          Nothing here yet. Click + to get started
        </div>
      </div>

      <div v-else>
        <div v-if="!loggedIn" class="fullscreen-centered">
          <div>
            <div @click="login" class="btn btn-outline btn-rounded caps mx-auto">Login</div>
          </div>
          <div v-if="showOfflineMessage">
            <p class="subtext m2">
              You appear to be offline, please try again later.
            </p>
          </div>
        </div>
        <div v-else-if="mynotes.length">
          <ul class="list-reset">
            <Preview v-for="note in mynotes" :key="note.id" :note="note" />
          </ul>

          <div v-if="mynotes.length" class="bottom-actions">
            <div v-on:click="loadMore" class="btn btn-outline btn-rounded caps mx-auto" v-bind:class="{ disabled: !showLoadMore }">
              <pulse-loader v-if="loadMoreBusy" :loading="true" :color="'#000000'" :size="'6px'"></pulse-loader>
              <div v-else>Load more</div>
            </div>
          </div>
        </div>
        <div v-else class="px2 py1 h6">
          Nothing here yet.
        </div>
      </div>

      <transition name="custom-classes-transition" enter-active-class="animated slideInRight" leave-active-class="animated slideOutRight">
        <add-new v-if="edit" :type="editType" :draft="draft" @close="close"/>
      </transition>
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import globals from '@/globals'
  import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
  import DraftPreview from '@/components/DraftPreview'
  import Preview from '@/components/Preview'
  import AddNew from '@/components/AddNew'

  export default {
    name: 'Mine',
    metaInfo: {
      title: 'My Field Notes'
    },
    data () {
      return {
        loading: false,
        showLoadMore: true,
        loadMoreBusy: false,
        tab: 'published',
        edit: false,
        addMenuVisible: false,
        showOfflineMessage: false
      }
    },
    mounted () {
      const tab = this.$route.query.t
      if (tab === 'drafts' || tab === 'published') {
        this.tab = tab
      }

      if (this.tab === 'drafts' && this.drafts.length === 0) {
        this.tab = 'published'
      }

      this.$store.subscribe((mutation) => {
        switch(mutation.type) {
          case 'ADD_DRAFT':
          case 'DELETE_DRAFT':
            if (this.drafts.length === 0) {
              if (this.tab === 'drafts') this.tab = 'published'
            } else {
              if (this.tab !== 'drafts') this.tab = 'drafts'
            }
            break;
        }
      })

      this.loading = true
      Promise.all([
        this.$store.dispatch('getDrafts'),
        this.$store.dispatch('getUserNotes')
      ]).finally(() => {
        if (this.mynotes.length < globals.pagination.perPage) {
          this.showLoadMore = false
        }
        this.loading = false
      })
    },
    computed: {
      draft () {
        return this.drafts.find(d => d.draftKey.toString() === this.edit)
      },
      editType () {
        return this.draft ? this.draft.resourceType : 'note'
      },
      loggedIn () {
        return this.$store.getters.loggedIn
      },
      hasUnsubmittedDrafts () {
        for (let i = 0; i< this.drafts.length; i++) {
          if (!this.drafts[i].noteSubmitted) return true
        }
        return false
      },
      ...mapState(['drafts', 'mynotes'])
    },
    methods: {
      uploadAll () {
        this.drafts.forEach(({ draftKey }) => {
          this.$store.commit('ADD_UPLOAD', draftKey)
        })
      },
      close () {
        this.$router.push('/mine')
      },
      login () {
        this.showOfflineMessage = false
        this.$store.dispatch('startLoginFlow')
        .catch(err => {
          this.showOfflineMessage = true
          throw err
        })        
      },
      async refresh () {
        try {
          await this.$store.dispatch('getUserNotes')
          if (this.mynotes.length < globals.pagination.perPage) {
            this.showLoadMore = false
          }
        } catch (err) {
          throw err
        }
      },
      async loadMore () {
        try {
          this.loadMoreBusy = true
          const before = this.mynotes.length
          await this.$store.dispatch('getNextPageOfUserNotes')
          const after = this.mynotes.length
          const diff = after - before
          if (diff < globals.pagination.perPage) {
            this.showLoadMore = false
          }
          this.loadMoreBusy = false
        } catch (err) {
          this.loadMoreBusy = false
          throw err
        }
      }
    },
    watch: {
      '$route.query.edit' (newVal) {
        this.edit = newVal
      }
    },
    created () {
      if (this.drafts.length) {
        this.tab = 'drafts'
      }
    },
    components: { DraftPreview, Preview, AddNew, PulseLoader }
  }
</script>

<style>
  .tab-link {
    border-bottom: 1.5px solid transparent;
  }

  .active {
    font-weight: bold;
    border-bottom: 1.5px solid;
  }

  .disabled {
    pointer-events: none;
  }
</style>
